import { useCallback, useEffect, useRef, useState } from "react";
import { CirclePlay, Pause, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "../../../../components/tailwind/dialog";
import { Button } from "../../../../components/tailwind/button";
const BOT_DELAY_MS = 900;
const USER_DELAY_MS = 650;
const TYPING_MS = 700;

const EMPTY_SCRIPT = {
  steps: [{ kind: "system", text: "Add workflow steps to preview the conversation." }],
  isEmpty: true
};

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex gap-1 items-center">
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  );
}

function ChatBubble({ message }) {
  if (message.kind === "system") {
    return (
      <div className="flex justify-center">
        <span className="text-[11px] text-gray-500 bg-white/70 px-3 py-1 rounded-full shadow-sm">{message.text}</span>
      </div>
    );
  }

  const isUser = message.kind === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 shadow-sm ${
          isUser
            ? "bg-[#DCF8C6] text-gray-900 rounded-tr-sm"
            : "bg-white text-gray-900 rounded-tl-sm"
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
        {message.buttons?.length > 0 && (
          <div className="mt-2 space-y-1.5 border-t border-gray-100 pt-2">
            {message.buttons.map((label) => (
              <div
                key={label}
                className="text-center text-xs font-medium text-[#0F6E56] bg-[#E1F5EE] rounded-lg py-1.5 px-2"
              >
                {label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function WorkflowPlayDemoDialog({ open, onOpenChange, workflowName, script }) {
  const activeScript = script?.steps?.length ? script : EMPTY_SCRIPT;
  const [visibleCount, setVisibleCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef(null);
  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const resetPlayback = useCallback(() => {
    clearTimers();
    setVisibleCount(0);
    setIsTyping(false);
    setIsPlaying(false);
    setProgress(0);
  }, [clearTimers]);

  const schedule = useCallback((fn, delay) => {
    const timerId = setTimeout(fn, delay);
    timersRef.current.push(timerId);
    return timerId;
  }, []);

  const playFrom = useCallback(
    (fromIndex) => {
      clearTimers();
      setIsPlaying(true);
      setVisibleCount(fromIndex);
      setProgress(fromIndex === 0 ? 0 : (fromIndex / activeScript.steps.length) * 100);

      const playStep = (index) => {
        if (index >= activeScript.steps.length) {
          setIsPlaying(false);
          setIsTyping(false);
          setProgress(100);
          return;
        }

        const step = activeScript.steps[index];
        const showTyping = step.kind === "bot";
        const delayBeforeShow = showTyping ? TYPING_MS : step.kind === "user" ? USER_DELAY_MS : BOT_DELAY_MS;

        if (showTyping) {
          setIsTyping(true);
        }

        schedule(() => {
          setIsTyping(false);
          setVisibleCount(index + 1);
          setProgress(((index + 1) / activeScript.steps.length) * 100);

          const gap = step.kind === "bot" ? BOT_DELAY_MS : step.kind === "user" ? USER_DELAY_MS : 500;
          schedule(() => playStep(index + 1), gap);
        }, delayBeforeShow);
      };

      playStep(fromIndex);
    },
    [activeScript.steps, clearTimers, schedule]
  );

  useEffect(() => {
    if (!open) {
      resetPlayback();
      return;
    }
    playFrom(0);
    return clearTimers;
  }, [open, activeScript.steps, playFrom, resetPlayback, clearTimers]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [visibleCount, isTyping]);

  const visibleMessages = activeScript.steps.slice(0, visibleCount);
  const headerTitle = workflowName?.trim() || activeScript.workflowName?.trim() || "Workflow Demo";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden gap-0 border-0 bg-transparent shadow-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Workflow demo preview</DialogTitle>
          <DialogDescription>Sample WhatsApp conversation demo.</DialogDescription>
        </DialogHeader>

        <div className="rounded-[28px] overflow-hidden border-[10px] border-gray-900 bg-gray-900 shadow-xl">
          <div className="bg-[#075E54] text-white px-4 pt-3 pb-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{headerTitle}</p>
                <p className="text-[11px] text-white/80 flex items-center gap-1.5">
                  <span className={`inline-block w-2 h-2 rounded-full ${isPlaying ? "bg-green-300 animate-pulse" : "bg-white/60"}`} />
                  {isPlaying ? "Playing demo…" : visibleCount >= activeScript.steps.length ? "Demo complete" : "Paused"}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-white hover:bg-white/10"
                  onClick={() => {
                    if (isPlaying) {
                      clearTimers();
                      setIsPlaying(false);
                      setIsTyping(false);
                      return;
                    }
                    playFrom(visibleCount);
                  }}
                  aria-label={isPlaying ? "Pause demo" : "Resume demo"}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <CirclePlay className="w-4 h-4" />}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-white hover:bg-white/10"
                  onClick={() => playFrom(0)}
                  aria-label="Restart demo"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="h-[420px] overflow-y-auto px-3 py-4 space-y-3"
            style={{
              backgroundColor: "#ECE5DD",
              backgroundImage:
                "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35) 0, transparent 40%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.25) 0, transparent 35%)"
            }}
          >
            {visibleMessages.map((message, index) => (
              <ChatBubble key={`${message.kind}-${index}-${message.text}`} message={message} />
            ))}
            {isTyping && <TypingIndicator />}
          </div>

          <div className="bg-gray-900 px-4 py-3">
            <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full bg-[#25D366] transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[10px] text-white/60 mt-2 text-center">Preview from current workflow</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
